"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ReactPayPalScriptOptions,
  PayPalScriptProvider,
  usePayPalScriptReducer,
  FUNDING,
  DISPATCH_ACTION,
} from "@paypal/react-paypal-js";
import { CartResponse, fetchCart } from "@/networking/carts.api";
import {
  addMissingApplePayRecurringSupportBreadcrumb,
  getMissingApplePayRecurringSupportDiagnostic,
  hasRecurringCartItems,
  isApplePayAvailableForCart,
  isApplePayRecurringTestOverrideEnabled,
} from "@/lib/apple-pay-recurring";
import {
  advanceSettle,
  initialSettleState,
  settleOnTimeout,
  type FundingRead,
} from "@/lib/paypal-funding-settle";
import type { PayPalApplePayConfig } from "@/hooks/usePPCPApplePay";

// Poll cadence + cap for the funding-eligibility settle. The cap is measured
// from when polling starts (already after isResolved), with margin over the
// observed ~100ms callable-gap — so it never fires on the slow cold
// script-load (that time elapses before polling begins).
const SETTLE_POLL_MS = 100;
const SETTLE_CAP_MS = 2000;

interface PaypalContextType {
  shouldShowVenmo: boolean;
  shouldShowPayLater: boolean;
  shouldShowApplePay: boolean;
  applePayConfig: PayPalApplePayConfig | null;
  isProviderReady: boolean;
  isRejected: boolean;
  isDecryptedApplePayViaPaypal?: boolean | null;
}

const PaypalContext = createContext<PaypalContextType>({
  shouldShowVenmo: false,
  shouldShowPayLater: false,
  shouldShowApplePay: false,
  applePayConfig: null,
  isProviderReady: false,
  isRejected: false,
  isDecryptedApplePayViaPaypal: null,
});

export const usePaypalContext = () => {
  const context = useContext(PaypalContext);
  if (!context) {
    throw new Error("usePaypalContext must be used within a PaypalProvider");
  }
  return context;
};

// Inner component that can safely use usePayPalScriptReducer
function PaypalProviderInner({
  children,
  shouldShowVenmo: shouldShowVenmoProp,
  shouldShowPayLater: shouldShowPayLaterProp,
  shouldShowApplePay: shouldShowApplePayProp,
  isDecryptedApplePayViaPaypal,
  sdkOptions,
}: {
  children: React.ReactNode;
  shouldShowVenmo: boolean;
  shouldShowPayLater: boolean;
  shouldShowApplePay: boolean;
  isDecryptedApplePayViaPaypal?: boolean | null;
  sdkOptions: ReactPayPalScriptOptions;
}) {
  const [{ isRejected, isResolved }, dispatch] = usePayPalScriptReducer();
  const [settle, setSettle] = useState(initialSettleState);
  const [applePayConfig, setApplePayConfig] =
    useState<PayPalApplePayConfig | null>(null);
  const [applePaySettled, setApplePaySettled] = useState(false);

  // Re-arm on options change: PayPalScriptProvider seeds its options once into
  // its reducer and does NOT reload when the `options` prop changes, so dispatch
  // RESET_OPTIONS to reload the SDK on a currency/merchant switch. That cycles
  // isResolved, so the settle effect (keyed on isResolved) re-arms.
  const lastSdkSigRef = useRef<string | null>(null);
  // useLayoutEffect (not useEffect): capture the options signature synchronously
  // after the commit that seeds PayPalScriptProvider, so a fast follow-up commit
  // with different options reliably triggers RESET_OPTIONS rather than being
  // coalesced past the first passive-effect flush.
  useLayoutEffect(() => {
    const o = sdkOptions as Record<string, unknown>;
    const sig = `${o.clientId ?? ""}|${o["merchant-id"] ?? ""}|${o.currency ?? ""}`;
    if (lastSdkSigRef.current === null) {
      lastSdkSigRef.current = sig; // first mount: ScriptProvider already loaded these
      return;
    }
    if (lastSdkSigRef.current !== sig) {
      lastSdkSigRef.current = sig;
      // Clear stale settled eligibility synchronously so old-currency flags
      // cannot mount against the reloading SDK before the new settle completes.
      setSettle(initialSettleState);
      setApplePaySettled(false);
      setApplePayConfig(null);
      dispatch({ type: DISPATCH_ACTION.RESET_OPTIONS, value: sdkOptions });
    }
  }, [sdkOptions, dispatch]);

  // Poll funding eligibility until callable + stable, then settle. The SDK can
  // report isResolved before isFundingEligible is callable; reading once and
  // latching that is the CURRENT-1850 race. Re-arms on every isResolved
  // transition (cold load and RESET_OPTIONS reload).
  useEffect(() => {
    if (!isResolved) return;

    setSettle(initialSettleState);
    let current = initialSettleState;

    const read = (): FundingRead => {
      const windowObj = window as unknown as Record<string, unknown>;
      const paypal = (windowObj.paypal_checkout_global || windowObj.paypal) as
        | { isFundingEligible?: (funding: string) => boolean }
        | undefined;
      if (paypal && typeof paypal.isFundingEligible === "function") {
        try {
          return {
            ready: true,
            venmo: paypal.isFundingEligible(FUNDING.VENMO),
            payLater: paypal.isFundingEligible(FUNDING.PAYLATER),
          };
        } catch {
          return { ready: false };
        }
      }
      return { ready: false };
    };

    const intervalId = setInterval(() => {
      current = advanceSettle(current, read());
      setSettle(current);
      if (current.settled) {
        clearInterval(intervalId);
        clearTimeout(capId);
      }
    }, SETTLE_POLL_MS);

    const capId = setTimeout(() => {
      clearInterval(intervalId);
      if (!current.settled) {
        current = settleOnTimeout(current);
        setSettle(current);
      }
    }, SETTLE_CAP_MS);

    return () => {
      clearInterval(intervalId);
      clearTimeout(capId);
    };
  }, [isResolved]);

  // Apple Pay: poll until paypal.Applepay is callable (never latch a
  // non-callable read), then a single awaited config() fetch. The cap stays
  // active until a result is committed, so a hung/slow config() still fails
  // closed at the cap and any late config() result is ignored. Skipped for
  // decrypted-flag merchants (VGS path). Re-arms on isResolved.
  useEffect(() => {
    if (!isResolved || !shouldShowApplePayProp || isDecryptedApplePayViaPaypal)
      return;

    setApplePaySettled(false);
    setApplePayConfig(null);
    let committed = false;

    const finish = (config: PayPalApplePayConfig | null) => {
      if (committed) return;
      committed = true;
      clearInterval(intervalId);
      clearTimeout(capId);
      setApplePayConfig(config);
      setApplePaySettled(true);
    };

    const tryFetch = () => {
      if (committed) return;
      const windowObj = window as unknown as Record<string, unknown>;
      const paypal = (windowObj.paypal_checkout_global || windowObj.paypal) as
        | {
            Applepay?: () => {
              config: () => Promise<PayPalApplePayConfig | null>;
            };
          }
        | undefined;
      if (paypal && typeof paypal.Applepay === "function") {
        clearInterval(intervalId); // stop polling; cap stays active until config settles
        try {
          Promise.resolve(paypal.Applepay().config())
            .then((config) => finish(config ?? null))
            .catch(() => finish(null));
        } catch {
          // Applepay()/config() can throw synchronously (before returning a
          // promise); fail closed so checkout never sees an uncaught SDK error.
          finish(null);
        }
      }
    };

    const intervalId = setInterval(tryFetch, SETTLE_POLL_MS);
    const capId = setTimeout(() => finish(null), SETTLE_CAP_MS);
    tryFetch();

    return () => {
      committed = true;
      clearInterval(intervalId);
      clearTimeout(capId);
    };
  }, [isResolved, shouldShowApplePayProp, isDecryptedApplePayViaPaypal]);

  // isResolved gating: during a RESET_OPTIONS reload isResolved briefly drops,
  // so the flags go false and stale buttons unmount until the new settle lands.
  const shouldShowVenmo =
    shouldShowVenmoProp && isResolved && settle.settled && settle.venmo;
  const shouldShowPayLater =
    shouldShowPayLaterProp && isResolved && settle.settled && settle.payLater;
  const shouldShowApplePay =
    shouldShowApplePayProp &&
    isResolved &&
    applePaySettled &&
    !!applePayConfig?.isEligible &&
    !isDecryptedApplePayViaPaypal;

  return (
    <PaypalContext.Provider
      value={{
        shouldShowVenmo,
        shouldShowPayLater,
        shouldShowApplePay,
        applePayConfig,
        isProviderReady: true,
        isRejected,
        isDecryptedApplePayViaPaypal,
      }}
    >
      {children}
    </PaypalContext.Provider>
  );
}

export function PaypalProvider({
  cartToken,
  paymentMethodsIncludes,
  children,
}: {
  cartToken: string;
  paymentMethodsIncludes?: string | null;
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const missingRecurringSupportBreadcrumbRef = useRef<string | null>(null);

  const cachedCartData = queryClient.getQueryData<CartResponse>([
    "cart",
    cartToken,
    paymentMethodsIncludes,
  ]);

  const { data: serverCartData, isLoading } = useQuery({
    queryKey: ["cart", cartToken, paymentMethodsIncludes],
    queryFn: () => fetchCart(cartToken, paymentMethodsIncludes ?? undefined),
    enabled: !!cartToken && !cachedCartData,
  });

  const cartData = cachedCartData || serverCartData;

  const paypalMethod = cartData?.cart?.available_payment_methods?.find(
    (method) => method.type === "paypal",
  );
  const clientId = paypalMethod?.client_id;
  const merchantId = paypalMethod?.merchant_id;

  const cartCurrency = cartData?.cart?.currency_code || "USD";

  const hasSubscriptions = hasRecurringCartItems(cartData?.cart?.items);
  const recurringTestOverride = isApplePayRecurringTestOverrideEnabled();
  const missingRecurringSupportDiagnostic = useMemo(
    () =>
      getMissingApplePayRecurringSupportDiagnostic({
        methods: paypalMethod ? [paypalMethod] : [],
        hasRecurringItems: hasSubscriptions,
        paypalApplePayMode: "standard",
        recurringTestOverride,
      }),
    [paypalMethod, hasSubscriptions, recurringTestOverride],
  );

  useEffect(() => {
    if (!missingRecurringSupportDiagnostic) return;

    const breadcrumbKey = `${cartData?.cart?.id ?? cartToken}:${missingRecurringSupportDiagnostic.key}`;
    if (missingRecurringSupportBreadcrumbRef.current === breadcrumbKey) return;

    missingRecurringSupportBreadcrumbRef.current = breadcrumbKey;
    addMissingApplePayRecurringSupportBreadcrumb(
      missingRecurringSupportDiagnostic,
    );
  }, [cartData?.cart?.id, cartToken, missingRecurringSupportDiagnostic]);

  const applePayAllowedForCart = isApplePayAvailableForCart({
    methods: paypalMethod ? [paypalMethod] : [],
    hasRecurringItems: hasSubscriptions,
    paypalApplePayMode: "standard",
    recurringTestOverride,
  });
  const isUSA =
    cartData?.cart?.country?.iso === "US" ||
    cartData?.cart?.country?.iso === "USA";

  const shouldShowVenmo = isUSA && !hasSubscriptions;
  const shouldShowPayLater = !hasSubscriptions;

  // Check if Apple Pay is potentially available (cart config + browser support + merchant ID)
  const shouldShowApplePay = useMemo(() => {
    if (typeof window === "undefined") return false;

    if (!applePayAllowedForCart) return false;
    if (!merchantId) return false;

    // Check if we're in a secure context
    if (!window.isSecureContext) return false;

    // Check for Safari's ApplePaySession OR PaymentRequest API (Chrome/Firefox)
    const hasSafariApplePay = !!(
      window.ApplePaySession && ApplePaySession.canMakePayments()
    );
    const hasPaymentRequest = "PaymentRequest" in window;

    return hasSafariApplePay || hasPaymentRequest;
  }, [applePayAllowedForCart, merchantId]);

  const initialOptions = useMemo((): ReactPayPalScriptOptions | null => {
    if (!clientId) {
      return null;
    }

    // Always request venmo eligibility from the SDK — PayPal does its own
    // geo/account checks via isFundingEligible(). The UI-level isUSA guard
    // (shouldShowVenmo) controls display. Without this, the one-shot SDK
    // load can permanently miss venmo if the cart country isn't resolved yet.
    const enabledFunding: string[] = ["venmo", "paylater"];

    const options: ReactPayPalScriptOptions = {
      clientId,
      components: "buttons,marks,funding-eligibility,messages,applepay",
      intent: "authorize",
      currency: cartCurrency,
      dataNamespace: "paypal_checkout_global",
      dataPageType: "checkout",
    };

    if (merchantId) {
      options["merchant-id"] = merchantId;
    }

    options["enable-funding"] = enabledFunding.join(",");

    return options;
  }, [clientId, merchantId, cartCurrency]);

  if (isLoading || !initialOptions) {
    return (
      <PaypalContext.Provider
        value={{
          shouldShowVenmo: false,
          shouldShowPayLater: false,
          shouldShowApplePay: false,
          applePayConfig: null,
          isProviderReady: false,
          isRejected: false,
        }}
      >
        {children}
      </PaypalContext.Provider>
    );
  }

  return (
    <PayPalScriptProvider options={initialOptions}>
      <PaypalProviderInner
        shouldShowVenmo={shouldShowVenmo}
        shouldShowPayLater={shouldShowPayLater}
        shouldShowApplePay={shouldShowApplePay}
        isDecryptedApplePayViaPaypal={
          paypalMethod?.decrypted_apple_pay_via_paypal
        }
        sdkOptions={initialOptions}
      >
        {children}
      </PaypalProviderInner>
    </PayPalScriptProvider>
  );
}
